/**
 * Guest session lifecycle primitives: where "is the page in front of the user" comes from,
 * and a clock that can be stopped while it is not.
 *
 * Both exist for the same reason. The guest's recovery budget is wall-clock, but everything
 * that evaluates it is a timer, and a backgrounded mobile browser throttles timers to about
 * one tick a minute. Measuring elapsed time across a background gap therefore blows any
 * budget shorter than the gap — which is all of them — so a backgrounded tab does not risk a
 * terminal disconnect, it guarantees one. Stopping the clock while hidden is the fix.
 *
 * The visibility source is injectable rather than read from `document` at the point of use
 * so the lifecycle can be driven deterministically in tests, and so a non-browser embedder
 * degrades to "always visible" rather than throwing.
 */

export type VisibilityState = 'visible' | 'hidden';

export type VisibilitySource = {
  /** Current state at call time. */
  current(): VisibilityState;
  /** Subscribe; returns an unsubscribe function. */
  subscribe(listener: (state: VisibilityState) => void): () => void;
};

type DocumentLike = {
  visibilityState?: string;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
};

const VISIBILITY_EVENT = 'visibilitychange';

/** Browser-backed source using document.visibilitychange. Falls back to always-visible. */
export function createDocumentVisibilitySource(): VisibilitySource {
  const doc = (globalThis as { document?: DocumentLike }).document;

  const current = (): VisibilityState => {
    try {
      return doc?.visibilityState === 'hidden' ? 'hidden' : 'visible';
    } catch {
      // A getter that throws is still a page the user can see; never report hidden on error,
      // because "hidden" is the state that suspends recovery.
      return 'visible';
    }
  };

  return {
    current,
    subscribe(listener: (state: VisibilityState) => void): () => void {
      if (!doc?.addEventListener || !doc.removeEventListener) return () => undefined;

      const handler = () => {
        try {
          listener(current());
        } catch {
          // A subscriber that throws must not break the event source for the others.
        }
      };

      try {
        doc.addEventListener(VISIBILITY_EVENT, handler);
      } catch {
        return () => undefined;
      }

      return () => {
        try {
          doc.removeEventListener?.(VISIBILITY_EVENT, handler);
        } catch {
          // Nothing useful to do; the listener is already unreachable to callers.
        }
      };
    },
  };
}

/**
 * A monotonic elapsed-time clock that does NOT advance while suspended.
 *
 * The recovery budget is wall-clock but its evaluators are timers, and mobile browsers
 * throttle background timers to ~1/min. Measuring elapsed time across a background gap
 * therefore blows any budget shorter than the gap. This clock stops instead.
 */
export type SuspendableClock = {
  /** Begin measuring from now. Resets any previous span. */
  start(): void;
  /** Elapsed milliseconds, excluding all suspended spans. 0 when never started. */
  elapsedMs(): number;
  /** Stop accumulating. Idempotent. */
  suspend(): void;
  /** Resume accumulating. Idempotent. Time spent suspended is not counted. */
  resume(): void;
  /** Forget the span entirely; elapsedMs() returns 0 until start() is called again. */
  reset(): void;
  /** True while suspended. */
  isSuspended(): boolean;
};

export function createSuspendableClock(nowMs: () => number = () => Date.now()): SuspendableClock {
  let running = false;
  let suspended = false;
  /** Milliseconds banked from spans that have already ended. */
  let accumulatedMs = 0;
  /** When the currently-running span began. Meaningless while suspended or stopped. */
  let spanStartedAtMs = 0;

  return {
    start(): void {
      running = true;
      suspended = false;
      accumulatedMs = 0;
      spanStartedAtMs = nowMs();
    },

    elapsedMs(): number {
      if (!running) return 0;
      if (suspended) return accumulatedMs;
      return accumulatedMs + Math.max(0, nowMs() - spanStartedAtMs);
    },

    suspend(): void {
      if (suspended) return;
      if (running) accumulatedMs += Math.max(0, nowMs() - spanStartedAtMs);
      suspended = true;
    },

    resume(): void {
      if (!suspended) return;
      suspended = false;
      spanStartedAtMs = nowMs();
    },

    reset(): void {
      running = false;
      suspended = false;
      accumulatedMs = 0;
      spanStartedAtMs = 0;
    },

    isSuspended(): boolean {
      return suspended;
    },
  };
}
